import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef, useEffect, useState, useCallback } from "react";
import ModuleHeader from "./ModuleHeader";
import ReactEcharts from "echarts-for-react";
import { rem2px, REM_CONFIG } from "@/utils/fontSize";
import { eamApi } from "@/api/eamApi";
const AssetClass = ({ className = "" }) => {
    const chartRef = useRef(null);
    const [currentTab, setCurrentTab] = useState(1); // 1:数量 2:金额
    const [isAutoPlay, setIsAutoPlay] = useState(true);
    const [tableData, setTableData] = useState([]);
    const [loading, setLoading] = useState(true);
    const amountData = tableData.map((item) => ({
        ...item,
        value: item.value,
        name: item.cname,
    })) || [];
    const quantityData = tableData.map((item) => ({
        ...item,
        value: item.count,
        name: item.cname,
    })) || [];
    // 配色方案
    const colors = [
        "#D94836",
        "#D97A36",
        "#D9A836",
        "#D9D936",
        "#A8D936",
        "#7AD936",
        "#36D97A",
        "#36D9A8",
        "#36D9D9",
        "#36A8D9",
        "#367AD9",
        "#3648D9",
        "#7A36D9",
        "#A836D9",
        "#D936D9",
        "#D936A8",
        "#D9367A",
    ];
    // 自动轮播逻辑
    useEffect(() => {
        let timer = null;
        if (isAutoPlay && tableData.length > 0) {
            timer = setInterval(() => {
                setCurrentTab((prev) => (prev === 1 ? 2 : 1));
            }, 5000);
        }
        return () => {
            if (timer)
                clearInterval(timer);
        };
    }, [isAutoPlay, tableData]);
    // 初始化表格数据
    useEffect(() => {
        getData();
    }, [currentTab]);
    /**
     * @description: 获取资产类别数据
     */
    const getData = async () => {
        try {
            setLoading(true);
            const res = await eamApi.category(currentTab);
            if (res.data) {
                setTableData(res.data);
            }
        }
        catch (error) {
            console.error("获取资产类别数据失败:", error);
        }
        finally {
            setLoading(false);
        }
    };
    // ECharts配置项（添加了图例截断处理）
    const getEchartsOption = useCallback(() => {
        const data = currentTab === 1 ? quantityData : amountData;
        return {
            tooltip: {
                trigger: "item",
                formatter: (params) => {
                    return `
          <div style="color:#fff;padding:${rem2px(0.2)}px;">
            <div>${params.data.name}</div>
            <div>${`${currentTab === 1 ? "数量" : "金额"}`}: ${params.value} ${`${currentTab === 1 ? " 项" : " 元"}`}</div>
            <div>占比: ${params.data.ratio}%</div>
          </div>
        `;
                },
                backgroundColor: "rgba(6, 32, 66, 0.8)",
                borderColor: "#36A8D9",
                borderWidth: 1,
                textStyle: {
                    color: "#FFFFFF",
                    fontSize: REM_CONFIG.fontSize.small,
                },
                padding: rem2px(0.04),
                borderRadius: 4,
            },
            legend: {
                orient: "vertical",
                right: rem2px(0.1),
                top: "center",
                // 关键修改：使用truncateText函数处理图例文本
                data: data.map((item) => item.name),
                textStyle: {
                    color: "#FFFFFF",
                    fontSize: REM_CONFIG.fontSize.small,
                },
                icon: "circle",
                itemWidth: rem2px(0.08),
                itemHeight: rem2px(0.08),
                itemGap: rem2px(0.06),
                formatter: function (name) {
                    if (name.length > 10) {
                        // 根据需要调整长度阈值
                        return name.slice(0, 12) + "..."; // 或者使用其他逻辑处理文本截断
                    }
                    else {
                        return name;
                    }
                },
            },
            graphic: {
                elements: [
                    {
                        type: "text",
                        right: rem2px(0.1),
                        style: {
                            text: currentTab === 1 ? "单位: 项" : "单位: 万元",
                            fill: "#FFFFFF",
                            fontSize: REM_CONFIG.fontSize.small,
                            fontWeight: "normal",
                        },
                    },
                ],
            },
            series: [
                {
                    type: "pie",
                    radius: ["40%", "70%"],
                    center: ["30%", "50%"],
                    label: {
                        show: true,
                        position: "inside",
                        formatter: (value) => {
                            return value.data.ratio + "%";
                        },
                        color: "#FFFFFF",
                        fontSize: REM_CONFIG.fontSize.small,
                    },
                    labelLine: { show: false },
                    data: data,
                    itemStyle: {
                        color: (params) => colors[params.dataIndex % colors.length],
                        borderWidth: 1,
                        borderColor: "rgba(6, 32, 66, 0.5)",
                    },
                    emphasis: {
                        itemStyle: {
                            shadowBlur: 10,
                            shadowColor: "rgba(54, 168, 217, 0.5)",
                        },
                    },
                },
            ],
            color: colors,
        };
    }, [currentTab, amountData, quantityData, loading]);
    useEffect(() => {
        const observer = new ResizeObserver(() => {
            if (chartRef.current) {
                chartRef.current.getEchartsInstance().setOption(getEchartsOption());
            }
        });
        if (document.documentElement) {
            observer.observe(document.documentElement);
        }
        return () => observer.disconnect();
    }, [getEchartsOption]);
    // 窗口大小变化时重绘图表
    useEffect(() => {
        const handleResize = () => {
            if (chartRef.current) {
                chartRef.current.getEchartsInstance().resize();
            }
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);
    return (_jsxs("div", { className: `
        relative 
        bg-gradient-to-t 
        from-[rgba(7,31,75,0.5)] 
        to-[rgba(6,32,66,0.5)] 
        min-h-0
        flex flex-col 
        ${className}
      `, onMouseEnter: () => setIsAutoPlay(false), onMouseLeave: () => setIsAutoPlay(true), children: [_jsx(ModuleHeader, { title: `资产类别分布-${currentTab === 1 ? "数量" : "金额"}` }), _jsx("div", { className: "flex-1 p-[0.2rem] min-h-0", children: _jsx(ReactEcharts, { ref: chartRef, option: getEchartsOption(), style: { width: "100%", height: "100%" }, opts: { renderer: "canvas" }, onChartReady: (chart) => {
                        if (tableData.length > 0) {
                            chart.setOption(getEchartsOption());
                        }
                    } }) }), tableData.length > 0 && (_jsx("div", { className: "tab-indicators flex justify-center items-center space-x-[0.2rem] mb-[0.02rem]", children: [1, 2].map((item) => (_jsx("span", { className: `w-[0.1rem] h-[0.1rem] rounded-full cursor-pointer 
                ${currentTab === item ? "bg-blue-500" : "bg-gray-400"}`, onClick: () => setCurrentTab(item) }, item))) }))] }));
};
export default AssetClass;
